import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Percent, Clock, Zap, Search, Tag } from 'lucide-react';

interface Deal {
  id: string;
  product_id: string | null;
  category_filter: string | null;
  discount_percentage: number;
  badge_text: string;
  start_date: string;
  end_date: string;
  active: boolean;
  featured: boolean;
  deal_type: string;
  min_quantity: number;
  is_flash_deal: boolean;
  priority: number;
  product?: {
    title: string;
    price_cents: number;
  };
}

interface PromoCode {
  id: string;
  code: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  min_purchase_amount: number | null;
  usage_limit: number | null;
  usage_count: number;
  start_date: string;
  end_date: string | null;
  active: boolean;
}

export function EnhancedDealsManagerV2() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'deals' | 'promo'>('deals');
  const [dealType, setDealType] = useState<'product' | 'category'>('product');
  const [vendorId, setVendorId] = useState<string | null>(null);
  
  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  
  // Deal form state
  const [dealForm, setDealForm] = useState({
    product_id: null as string | null,
    category_filter: null as string | null,
    discount_percentage: 10,
    badge_text: 'PROMO',
    start_date: new Date().toISOString().slice(0, 16),
    end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    active: true,
    featured: false,
    deal_type: 'percentage',
    min_quantity: 1,
    is_flash_deal: false,
    priority: 0
  });

  // Promo code form state
  const [promoForm, setPromoForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    min_purchase_amount: 0,
    usage_limit: null as number | null,
    start_date: new Date().toISOString().slice(0, 16),
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
    active: true
  });

  useEffect(() => {
    fetchVendorId();
  }, []);

  useEffect(() => {
    if (vendorId) {
      fetchDeals();
      fetchPromoCodes();
      fetchCategories();
    }
  }, [vendorId]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [productSearch, selectedCategoryFilter, vendorId]);

  // Mapping des catégories entre affichage et base de données
  const categoryDisplayToDb: { [key: string]: string } = {
    'Électronique': 'electronique',
    'Mode': 'mode',
    'Maison & Jardin': 'maison',
    'Beauté & Santé': 'beaute',
    'Sport & Loisirs': 'sport',
    'Livres': 'livres'
  };

  const categoryDbToDisplay: { [key: string]: string } = {
    'electronique': 'Électronique',
    'mode': 'Mode',
    'maison': 'Maison & Jardin',
    'beaute': 'Beauté & Santé',
    'sport': 'Sport & Loisirs',
    'livres': 'Livres'
  };

  const fetchVendorId = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // L'ID de l'utilisateur est utilisé comme vendor_id dans la table products
      setVendorId(user.id);
    } catch (error) {
      console.error('Error fetching vendor ID:', error);
    }
  };

  const fetchCategories = async () => {
    if (!vendorId) return;
    
    try {
      // Récupérer toutes les catégories uniques des produits du vendeur
      const { data, error } = await supabase
        .from('products')
        .select('category')
        .eq('vendor_id', vendorId);
      
      if (error) {
        console.error('Error fetching categories:', error);
        return;
      }
      
      if (data) {
        // Extraire les catégories uniques
        const uniqueCategories = [...new Set(data.map(p => p.category).filter(Boolean))];
        
        // Mapper vers les noms d'affichage, ou utiliser la valeur brute si pas de mapping
        const displayCategories = uniqueCategories.map(cat => 
          categoryDbToDisplay[cat as string] || cat
        );
        
        setCategories(displayCategories.sort() as string[]);
      }
    } catch (error) {
      console.error('Exception in fetchCategories:', error);
    }
  };

  const fetchProducts = async () => {
    if (!vendorId) return;
    
    try {
      let query = supabase
        .from('products')
        .select('id, title, price_cents, category')
        .eq('vendor_id', vendorId)
        .eq('active', true);

      if (productSearch) {
        query = query.ilike('title', `%${productSearch}%`);
      }

      if (selectedCategoryFilter) {
        // Convertir le nom d'affichage vers la valeur DB
        const dbCategory = categoryDisplayToDb[selectedCategoryFilter] || selectedCategoryFilter;
        query = query.eq('category', dbCategory);
      }

      const { data, error } = await query.order('title').limit(100);
      
      if (error) {
        console.error('Error fetching products:', error);
        return;
      }
      
      if (data) setProducts(data);
    } catch (error) {
      console.error('Exception in fetchProducts:', error);
    }
  };

  const fetchDeals = async () => {
    if (!vendorId) return;
    
    try {
      // Récupérer tous les deals (y compris ceux sans product_id)
      const { data: allDeals, error } = await supabase
        .from('deals')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching deals:', error);
        toast.error('Erreur lors du chargement des deals');
        return;
      }

      // Pour chaque deal, récupérer le produit si product_id existe
      const dealsWithProducts = await Promise.all(
        (allDeals || []).map(async (deal: any) => {
          if (deal.product_id) {
            const { data: product } = await supabase
              .from('products')
              .select('title, price_cents, vendor_id')
              .eq('id', deal.product_id)
              .single();
            
            // Filtrer uniquement les deals du vendeur actuel
            if (product && product.vendor_id === vendorId) {
              return { ...deal, product };
            }
            return null; // Deal d'un autre vendeur
          }
          // Les deals de catégorie (sans product_id) sont inclus
          return deal;
        })
      );

      // Filtrer les nulls (deals d'autres vendeurs)
      const filteredDeals = dealsWithProducts.filter(d => d !== null);
      setDeals(filteredDeals as any);
    } catch (error) {
      console.error('Exception in fetchDeals:', error);
      toast.error('Erreur lors du chargement des deals');
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false});
      
      if (error) {
        console.error('Error fetching promo codes:', error);
        toast.error('Erreur lors du chargement des codes promo');
        return;
      }
      
      if (data) setPromoCodes(data as any);
    } catch (error) {
      console.error('Exception in fetchPromoCodes:', error);
      toast.error('Erreur lors du chargement des codes promo');
    }
  };

  const createDeal = async () => {
    if (dealType === 'product' && !dealForm.product_id) {
      toast.error('Veuillez sélectionner un produit');
      return;
    }
    if (dealType === 'category' && !dealForm.category_filter) {
      toast.error('Veuillez sélectionner une catégorie');
      return;
    }

    // Convertir la catégorie d'affichage vers la valeur DB si nécessaire
    const categoryValue = dealType === 'category' && dealForm.category_filter
      ? categoryDisplayToDb[dealForm.category_filter] || dealForm.category_filter
      : null;

    const dealData = {
      ...dealForm,
      product_id: dealType === 'product' ? dealForm.product_id : null,
      category_filter: categoryValue
    };

    const { error } = await supabase.from('deals').insert([dealData]);
    
    if (error) {
      console.error(error);
      toast.error('Erreur lors de la création du deal');
    } else {
      toast.success('Deal créé avec succès');
      fetchDeals();
      // Reset form
      setDealForm({
        ...dealForm,
        product_id: null,
        category_filter: null,
        discount_percentage: 10,
        badge_text: 'PROMO'
      });
      setProductSearch('');
    }
  };

  const createPromoCode = async () => {
    if (!promoForm.code) {
      toast.error('Veuillez entrer un code promo');
      return;
    }

    const { error } = await supabase.from('promo_codes').insert([promoForm]);
    
    if (error) {
      console.error(error);
      toast.error('Erreur lors de la création du code promo');
    } else {
      toast.success('Code promo créé avec succès');
      fetchPromoCodes();
      // Reset form
      setPromoForm({
        ...promoForm,
        code: '',
        description: '',
        discount_value: 10
      });
    }
  };

  const deleteDeal = async (id: string) => {
    const { error } = await supabase.from('deals').delete().eq('id', id);
    if (error) {
      toast.error('Erreur lors de la suppression');
    } else {
      toast.success('Deal supprimé');
      fetchDeals();
    }
  };

  const deletePromoCode = async (id: string) => {
    const { error } = await supabase.from('promo_codes').delete().eq('id', id);
    if (error) {
      toast.error('Erreur lors de la suppression');
    } else {
      toast.success('Code promo supprimé');
      fetchPromoCodes();
    }
  };

  const toggleDealActive = async (id: string, active: boolean) => {
    const { error } = await supabase
      .from('deals')
      .update({ active: !active })
      .eq('id', id);
    if (!error) fetchDeals();
  };

  const togglePromoActive = async (id: string, active: boolean) => {
    const { error } = await supabase
      .from('promo_codes')
      .update({ active: !active })
      .eq('id', id);
    if (!error) fetchPromoCodes();
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Gestion des Promotions</h1>
          <p className="text-muted-foreground">Créez des deals et codes promo pour booster vos ventes</p>
        </div>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
          <TabsList>
            <TabsTrigger value="deals">
              <Percent className="w-4 h-4 mr-2" />
              Deals
            </TabsTrigger>
            <TabsTrigger value="promo">
              <Tag className="w-4 h-4 mr-2" />
              Codes Promo
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {activeTab === 'deals' ? (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Create Deal Form */}
          <Card>
            <CardHeader>
              <CardTitle>Créer un nouveau deal</CardTitle>
              <CardDescription>
                Appliquez une réduction sur un produit spécifique ou sur toute une catégorie
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Deal Type Selection */}
              <div className="space-y-2">
                <Label>Type de deal</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={dealType === 'product' ? 'default' : 'outline'}
                    onClick={() => {
                      setDealType('product');
                      setDealForm({...dealForm, product_id: null, category_filter: null});
                    }}
                    className="flex-1"
                  >
                    Produit spécifique
                  </Button>
                  <Button
                    type="button"
                    variant={dealType === 'category' ? 'default' : 'outline'}
                    onClick={() => {
                      setDealType('category');
                      setDealForm({...dealForm, product_id: null, category_filter: null});
                    }}
                    className="flex-1"
                  >
                    Catégorie complète
                  </Button>
                </div>
              </div>

              {dealType === 'product' ? (
                <div className="space-y-3">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      Rechercher un produit
                    </Label>
                    <Input
                      placeholder="Tapez pour rechercher..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                    />
                  </div>
                  
                  <div>
                    <Label>Filtrer par catégorie</Label>
                    <Select value={selectedCategoryFilter || 'all'} onValueChange={(value) => setSelectedCategoryFilter(value === 'all' ? '' : value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Toutes les catégories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes les catégories</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Produit ({products.length} résultats)</Label>
                    <Select 
                      value={dealForm.product_id || ''} 
                      onValueChange={(value) => setDealForm({...dealForm, product_id: value || null})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un produit" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {products.length === 0 ? (
                          <div className="p-2 text-sm text-muted-foreground">Aucun produit trouvé</div>
                        ) : (
                          products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.title} ({p.category || 'Sans catégorie'})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    {products.length === 100 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Affichage limité à 100 produits. Affinez votre recherche pour plus de précision.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <Label>Catégorie</Label>
                  <Select value={dealForm.category_filter || undefined} onValueChange={(value) => setDealForm({...dealForm, category_filter: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une catégorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground mt-2">
                    💡 Le deal s'appliquera automatiquement à tous les produits de cette catégorie
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Réduction (%)</Label>
                  <Input
                    type="number"
                    value={dealForm.discount_percentage}
                    onChange={(e) => setDealForm({...dealForm, discount_percentage: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Priorité (0-100)</Label>
                  <Input
                    type="number"
                    value={dealForm.priority}
                    onChange={(e) => setDealForm({...dealForm, priority: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div>
                <Label>Badge</Label>
                <Input
                  value={dealForm.badge_text}
                  onChange={(e) => setDealForm({...dealForm, badge_text: e.target.value})}
                  placeholder="Ex: PROMO, -50%, NOUVEAU"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date début</Label>
                  <Input
                    type="datetime-local"
                    value={dealForm.start_date}
                    onChange={(e) => setDealForm({...dealForm, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Date fin</Label>
                  <Input
                    type="datetime-local"
                    value={dealForm.end_date}
                    onChange={(e) => setDealForm({...dealForm, end_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Label>Deal Flash ⚡</Label>
                <Switch
                  checked={dealForm.is_flash_deal}
                  onCheckedChange={(checked) => setDealForm({...dealForm, is_flash_deal: checked})}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label>Mis en avant ⭐</Label>
                <Switch
                  checked={dealForm.featured}
                  onCheckedChange={(checked) => setDealForm({...dealForm, featured: checked})}
                />
              </div>

              <Button onClick={createDeal} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Créer le deal
              </Button>
            </CardContent>
          </Card>

          {/* Deals List */}
          <Card>
            <CardHeader>
              <CardTitle>Deals actifs ({deals.length})</CardTitle>
              <CardDescription>Gérez vos promotions en cours</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {deals.map((deal) => (
                  <div key={deal.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold">
                          {deal.product_id 
                            ? deal.product?.title 
                            : `📁 Catégorie: ${categoryDbToDisplay[deal.category_filter || ''] || deal.category_filter}`}
                        </p>
                        {deal.is_flash_deal && (
                          <Badge variant="destructive" className="text-xs">
                            <Zap className="w-3 h-3 mr-1" />
                            FLASH
                          </Badge>
                        )}
                        {deal.featured && <Badge variant="secondary">⭐ Vedette</Badge>}
                        {!deal.product_id && (
                          <Badge>Catégorie</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        -{deal.discount_percentage}% • {deal.badge_text} • Priorité: {deal.priority}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        <Clock className="w-3 h-3 inline mr-1" />
                        Jusqu'au {new Date(deal.end_date).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={deal.active}
                        onCheckedChange={() => toggleDealActive(deal.id, deal.active)}
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteDeal(deal.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {deals.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    Aucun deal créé pour le moment
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Create Promo Code Form */}
          <Card>
            <CardHeader>
              <CardTitle>Créer un code promo</CardTitle>
              <CardDescription>Code utilisable au checkout par vos clients</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Code</Label>
                <Input
                  value={promoForm.code}
                  onChange={(e) => setPromoForm({...promoForm, code: e.target.value.toUpperCase()})}
                  placeholder="Ex: WELCOME10, BLACK25"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={promoForm.description}
                  onChange={(e) => setPromoForm({...promoForm, description: e.target.value})}
                  placeholder="Description du code promo"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type de réduction</Label>
                  <Select value={promoForm.discount_type} onValueChange={(value) => setPromoForm({...promoForm, discount_type: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Pourcentage</SelectItem>
                      <SelectItem value="fixed_amount">Montant fixe</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valeur</Label>
                  <Input
                    type="number"
                    value={promoForm.discount_value}
                    onChange={(e) => setPromoForm({...promoForm, discount_value: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Achat minimum (centimes)</Label>
                  <Input
                    type="number"
                    value={promoForm.min_purchase_amount}
                    onChange={(e) => setPromoForm({...promoForm, min_purchase_amount: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Utilisations max</Label>
                  <Input
                    type="number"
                    value={promoForm.usage_limit || ''}
                    onChange={(e) => setPromoForm({...promoForm, usage_limit: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Illimité"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date début</Label>
                  <Input
                    type="datetime-local"
                    value={promoForm.start_date}
                    onChange={(e) => setPromoForm({...promoForm, start_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Date fin</Label>
                  <Input
                    type="datetime-local"
                    value={promoForm.end_date}
                    onChange={(e) => setPromoForm({...promoForm, end_date: e.target.value})}
                  />
                </div>
              </div>

              <Button onClick={createPromoCode} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Créer le code promo
              </Button>
            </CardContent>
          </Card>

          {/* Promo Codes List */}
          <Card>
            <CardHeader>
              <CardTitle>Codes promo ({promoCodes.length})</CardTitle>
              <CardDescription>Codes utilisables au checkout</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {promoCodes.map((promo) => (
                  <div key={promo.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-lg">{promo.code}</p>
                        <Badge variant="outline">
                          {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `${promo.discount_value}€`}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{promo.description || 'Pas de description'}</p>
                      <p className="text-xs text-muted-foreground">
                        Utilisé {promo.usage_count}/{promo.usage_limit || '∞'} fois
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={promo.active}
                        onCheckedChange={() => togglePromoActive(promo.id, promo.active)}
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deletePromoCode(promo.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {promoCodes.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    Aucun code promo créé pour le moment
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
